import { useMemo } from "react";
import { Loader2, Phone, SearchX, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import type { DisparoLead, DisparoLeadFilters } from "@/lib/disparos-types";
import { formatPhoneForDisplay } from "@/lib/format-phone";
import { cn } from "@/lib/utils";

interface DisparoLeadListProps {
  leads: DisparoLead[];
  filters: DisparoLeadFilters;
  loading: boolean;
  selectedIds: Set<string>;
  selectedCount: number;
  onToggle: (leadId: string, checked: boolean) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
}

type ListLayout = "grouped-by-stage" | "alphabetical" | "with-funnel-meta";

function getListLayout(filters: DisparoLeadFilters): ListLayout {
  if (filters.stageSlug || filters.tagIds.length > 0) return "alphabetical";
  if (filters.funnelId) return "grouped-by-stage";
  return "with-funnel-meta";
}

interface StageSection {
  key: string;
  title: string;
  orderIndex: number;
  leads: DisparoLead[];
}

function groupLeadsByStage(leads: DisparoLead[]): StageSection[] {
  const sections = new Map<string, StageSection>();

  for (const lead of leads) {
    const key = lead.stage || "__sem_coluna__";
    if (!sections.has(key)) {
      sections.set(key, {
        key,
        title: lead.stage_name || lead.stage || "Sem coluna",
        orderIndex: lead.stage_order_index ?? Number.MAX_SAFE_INTEGER,
        leads: [],
      });
    }
    sections.get(key)!.leads.push(lead);
  }

  return [...sections.values()]
    .map((section) => ({
      ...section,
      leads: [...section.leads].sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", "pt-BR", { sensitivity: "base" }),
      ),
    }))
    .sort((a, b) => a.orderIndex - b.orderIndex || a.title.localeCompare(b.title, "pt-BR"));
}

function LeadRow({
  lead,
  checked,
  showFunnelMeta,
  onToggle,
}: {
  lead: DisparoLead;
  checked: boolean;
  showFunnelMeta: boolean;
  onToggle: (leadId: string, checked: boolean) => void;
}) {
  const funnelMeta =
    showFunnelMeta && (lead.funnel_name || lead.stage_name)
      ? [lead.funnel_name, lead.stage_name].filter(Boolean).join(" → ")
      : null;

  return (
    <label
      className={cn(
        "flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-muted/50 transition-colors",
        checked && "bg-primary/5",
      )}
    >
      <Checkbox checked={checked} onCheckedChange={(v) => onToggle(lead.id, v === true)} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{lead.name || "Sem nome"}</p>
        <div className="flex items-center gap-1.5 min-w-0 text-xs text-muted-foreground">
          <span className="truncate shrink-0">{formatPhoneForDisplay(lead.phone)}</span>
          {funnelMeta && (
            <>
              <span className="text-muted-foreground/40 shrink-0">·</span>
              <span className="truncate">{funnelMeta}</span>
            </>
          )}
        </div>
      </div>
    </label>
  );
}

export function DisparoLeadList({
  leads,
  filters,
  loading,
  selectedIds,
  selectedCount,
  onToggle,
  onSelectAll,
  onDeselectAll,
}: DisparoLeadListProps) {
  const layout = getListLayout(filters);

  const stageSections = useMemo(
    () => (layout === "grouped-by-stage" ? groupLeadsByStage(leads) : []),
    [layout, leads],
  );

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground gap-3">
        <Loader2 className="w-5 h-5 animate-spin" />
        <p className="text-sm">Carregando leads com telefone...</p>
      </div>
    );
  }

  const visibleSelectedCount = leads.filter((lead) => selectedIds.has(lead.id)).length;
  const allSelected = leads.length > 0 && visibleSelectedCount === leads.length;

  return (
    <div className="flex flex-col gap-3 flex-1 min-h-0 h-full">
      <div className="flex items-start justify-between gap-3 flex-wrap shrink-0">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-primary shrink-0" />
            <span className="text-sm font-medium">{selectedCount} selecionado(s)</span>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <Phone className="w-3 h-3" />
              {leads.length} lead(s) com telefone válido
            </span>
          </div>
        </div>
        <div className="flex gap-1 shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={onSelectAll}
            disabled={leads.length === 0 || allSelected}
          >
            Selecionar todos
          </Button>
          <Button variant="ghost" size="sm" onClick={onDeselectAll} disabled={selectedCount === 0}>
            Limpar seleção
          </Button>
        </div>
      </div>

      {leads.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 py-10 px-4 rounded-lg bg-muted/40 text-center">
          <SearchX className="w-8 h-8 text-muted-foreground/60" />
          <p className="text-sm font-medium">Nenhum lead encontrado</p>
          <p className="text-xs text-muted-foreground max-w-sm leading-relaxed">
            Não há leads com telefone válido para os filtros atuais. Tente limpar os filtros, buscar
            por outro nome ou telefone, ou escolher outro funil.
          </p>
        </div>
      ) : (
        <div className="flex-1 min-h-0 overflow-y-auto rounded-lg border">
          {layout === "grouped-by-stage" ? (
            <div className="divide-y">
              {stageSections.map((section) => (
                <section key={section.key}>
                  <div className="sticky top-0 z-10 border-b bg-background/95 px-3 py-2 backdrop-blur-sm">
                    <p className="text-xs font-medium text-muted-foreground">{section.title}</p>
                  </div>
                  <div className="divide-y">
                    {section.leads.map((lead) => (
                      <LeadRow
                        key={lead.id}
                        lead={lead}
                        checked={selectedIds.has(lead.id)}
                        showFunnelMeta={false}
                        onToggle={onToggle}
                      />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          ) : (
            <div className="divide-y">
              {leads.map((lead) => (
                <LeadRow
                  key={lead.id}
                  lead={lead}
                  checked={selectedIds.has(lead.id)}
                  showFunnelMeta={layout === "with-funnel-meta"}
                  onToggle={onToggle}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
